import { useCallback, useEffect, useRef } from 'react';

interface DebouncedActionOptions {
    /**
     * Run a pending call when the component unmounts instead of dropping it. Use it only when
     * the action works from the arguments given to `schedule`, because the component state it
     * could otherwise read (form fields) is gone by then.
     */
    flushOnUnmount?: boolean;
}

/**
 * Runs `action` after a delay, restarting the delay on every `schedule` call. The latest
 * `action` runs with the arguments of the last `schedule` call. By default a pending run is
 * dropped when the component unmounts; with `flushOnUnmount` it runs right away instead, so a
 * change made just before the user leaves is still saved.
 */
export const useDebouncedAction = <Args extends unknown[] = []>(
    action: (...args: Args) => unknown,
    options: DebouncedActionOptions = {}
) => {
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingArgs = useRef<Args | null>(null);
    const latest = useRef(action);
    latest.current = action;
    const flushOnUnmount = useRef(Boolean(options.flushOnUnmount));
    flushOnUnmount.current = Boolean(options.flushOnUnmount);

    const cancel = useCallback(() => {
        if (timer.current !== null) {
            clearTimeout(timer.current);
            timer.current = null;
        }
        pendingArgs.current = null;
    }, []);

    const flush = useCallback(() => {
        const args = pendingArgs.current;
        if (timer.current === null || args === null) return;
        cancel();
        void latest.current(...args);
    }, [cancel]);

    const schedule = useCallback(
        (delayMs: number, ...args: Args) => {
            cancel();
            pendingArgs.current = args;
            timer.current = setTimeout(() => {
                timer.current = null;
                pendingArgs.current = null;
                void latest.current(...args);
            }, delayMs);
        },
        [cancel]
    );

    useEffect(() => () => (flushOnUnmount.current ? flush() : cancel()), [cancel, flush]);

    return { schedule, cancel, flush };
};
